/**
 * Run command - streaming task execution via Sandbox SSE
 *
 * Dispatches a coding agent task to the Cloudflare Sandbox Worker and streams
 * output in real-time via Server-Sent Events. Unlike exec (fire-and-forget),
 * this command blocks until the task completes and shows live output.
 */

import chalk from 'chalk';
import type { TaskOptions } from '@spacecode/shared/remote';
import { validateRepo, validateBranch, validatePrompt } from '../utils/validation.js';
import { generateTaskId } from '../utils/task.js';
import { setupRun } from '../utils/run-setup.js';
import { buildRemoteTask, statusFromResult, errorTaskStatus } from '../utils/remote-task.js';

/**
 * Run a task on a Cloudflare Sandbox and stream output in real-time via SSE.
 *
 * Flow:
 * 1. Validate inputs
 * 2. Gather config files and env vars
 * 3. Create sandbox client and call /api/run
 * 4. Stream SSE events to console
 * 5. Save task record
 */
export async function run(options: TaskOptions): Promise<void> {
  const taskId = generateTaskId();
  const validRepo = validateRepo(options.repo);
  const validPrompt = validatePrompt(options.prompt);
  const validBranch = options.branch ? validateBranch(options.branch) : undefined;

  // Set up SIGINT handler to abort the in-flight SSE request
  const abortController = new AbortController();
  const sigintHandler = () => {
    console.log(chalk.dim('\nReceived SIGINT, aborting stream...'));
    abortController.abort();
  };
  process.on('SIGINT', sigintHandler);

  console.log(chalk.bold('\nspacecode run\n'));
  console.log(chalk.dim(`  Task:   ${taskId}`));
  console.log(chalk.dim(`Repo:   ${validRepo}`));
  console.log(chalk.dim(`Prompt: ${validPrompt.substring(0, 60)}${validPrompt.length > 60 ? '...' : ''}`));
  if (validBranch) console.log(chalk.dim(`Branch: ${validBranch}`));
  console.log('');

  try {
    const { client, envVars } = await setupRun({ proxyMode: options.proxyMode });

    let exitCode = 0;
    let success = true;

    console.log(chalk.dim('---'.repeat(20)));

    // Keepalive timeout: abort if no SSE events arrive for 5 minutes
    const KEEPALIVE_TIMEOUT_MS = 5 * 60 * 1000;
    let keepaliveTimer: ReturnType<typeof setTimeout> | null = null;

    const resetKeepalive = () => {
      if (keepaliveTimer) clearTimeout(keepaliveTimer);
      keepaliveTimer = setTimeout(() => {
        console.error(chalk.yellow('\nStream stalled (no data for 5 minutes), aborting...'));
        abortController.abort();
      }, KEEPALIVE_TIMEOUT_MS);
    };

    resetKeepalive();

    // Stream SSE events from sandbox
    try {
      await client.run(
        {
          repo: validRepo,
          prompt: validPrompt,
          envVars,
          branch: validBranch,
          noCommit: options.noCommit,
          idleTimeoutMinutes: options.idleTimeout,
        },
        (event) => {
          resetKeepalive();
          switch (event.type) {
            case 'status':
              console.log(chalk.dim(`[${event.data.phase}]`));
              break;
            case 'output':
              process.stdout.write(event.data.text);
              break;
            case 'done':
              exitCode = event.data.exitCode;
              success = event.data.success;
              if (event.data.diff) {
                console.log(chalk.dim('\nDiff:'));
                console.log(event.data.diff);
              }
              break;
            case 'error':
              console.error(chalk.red(`\nError: ${event.data.message}`));
              success = false;
              exitCode = 1;
              break;
          }
        },
        abortController.signal,
      );
    } finally {
      if (keepaliveTimer) clearTimeout(keepaliveTimer);
    }

    console.log(chalk.dim('---'.repeat(20)));

    // Save task record
    const { addTask } = await import('../config.js');
    const task = buildRemoteTask({
      id: taskId,
      repo: validRepo,
      prompt: validPrompt,
      mode: 'stream',
      status: statusFromResult(success),
      exitCode,
      branch: validBranch,
    });
    await addTask(task);

    if (success) {
      console.log(chalk.green('Task completed successfully.'));
    } else {
      console.log(chalk.red(`Task failed (exit code ${exitCode}).`));
      process.exit(exitCode || 1);
    }
  } catch (error) {
    const isAbort = error instanceof Error && error.name === 'AbortError';

    // Save task record
    try {
      const { addTask } = await import('../config.js');
      const task = buildRemoteTask({
        id: taskId,
        repo: validRepo,
        prompt: validPrompt,
        mode: 'stream',
        status: errorTaskStatus(isAbort),
        error: isAbort ? 'Cancelled by user' : (error instanceof Error ? error.message : String(error)),
      });
      await addTask(task);
    } catch (saveErr) {
      console.warn(`Failed to save task record: ${saveErr instanceof Error ? saveErr.message : String(saveErr)}`);
    }

    if (isAbort) {
      console.log(chalk.yellow('\nStream cancelled.'));
      process.exit(130);
    } else {
      console.error(chalk.red(`\nError: ${error instanceof Error ? error.message : String(error)}`));
      process.exit(1);
    }
  } finally {
    process.removeListener('SIGINT', sigintHandler);
  }
}
